import { Injectable } from '@angular/core';

const TAB_ID_KEY = 'sensika-tab-id';
const COMPANY_ID_KEY = 'sensika-tab-company-id';

/**
 * Manages per-tab session identity so each browser tab can maintain
 * an independent active company context.
 *
 * Uses `sessionStorage` which is scoped per-tab for freshly opened tabs.
 * On "Duplicate Tab", sessionStorage is cloned — the new tab inherits
 * the company context but gets a fresh tabId (via reload detection).
 */
@Injectable({ providedIn: 'root' })
export class TabSessionService {
  readonly tabId: string;

  constructor() {
    const existingTabId = sessionStorage.getItem(TAB_ID_KEY);

    const isReload = (
      performance.getEntriesByType(
        'navigation',
      ) as PerformanceNavigationTiming[]
    ).some((entry) => entry.type === 'reload');

    if (existingTabId && isReload) {
      this.tabId = existingTabId;
    } else {
      // Use native API if available, otherwise use fallback
      this.tabId = this.generateUUID();
    }

    sessionStorage.setItem(TAB_ID_KEY, this.tabId);
  }

  private generateUUID(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    // Fallback for non-secure contexts or older browsers
    return (([1e7] as any) + -1e3 + -4e3 + -8e3 + -1e11).replace(
      /[018]/g,
      (c: any) =>
        (
          c ^
          (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
        ).toString(16),
    );
  }

  getActiveCompanyId(): string | null {
    return sessionStorage.getItem(COMPANY_ID_KEY);
  }

  setActiveCompanyId(companyId: string): void {
    sessionStorage.setItem(COMPANY_ID_KEY, companyId);
  }

  clearActiveCompanyId(): void {
    sessionStorage.removeItem(COMPANY_ID_KEY);
  }

  clear(): void {
    sessionStorage.removeItem(TAB_ID_KEY);
    sessionStorage.removeItem(COMPANY_ID_KEY);
  }
}
